import { getWorkerUrl } from '@affine/env/worker';
import { OpClient } from '@toeverything/infra/op';

import type {
  CsvDatabaseImportWorkerPreflightRequest,
  CsvDatabaseImportWorkerStartRequest,
  DatabaseImportParserProgress,
  DatabaseImportParserWorkerOps,
  DatabaseImportWorkerStartResult,
  ExcelDatabaseImportWorkerPreflightRequest,
  ExcelDatabaseImportWorkerStartRequest,
} from './database-import-parser-worker-types';
import { ImportLimitError, readImportLimitDetails } from './import-limit-error';
import type {
  ImportFilePreflight,
  ImportSheetPreflight,
} from './import-preflight';

// A cold parser worker has to download, compile and initialize the workbook
// parser before it can answer the ping. One second is too short on otherwise
// healthy clients under load and turns a recoverable cold start into a failed
// import preview.
const WORKER_READY_TIMEOUT_MS = 5_000;

export type DatabaseImportParserProgressHandler = (
  progress: DatabaseImportParserProgress
) => void;

export class DatabaseImportParserWorkerUnavailableError extends Error {
  constructor() {
    super('Database import parser is unavailable.');
    this.name = 'DatabaseImportParserWorkerUnavailableError';
  }
}

type ParserWorkerState = {
  worker: Worker;
  client: OpClient<DatabaseImportParserWorkerOps>;
  fatalPromise: Promise<never>;
  fatalError: Error | null;
  readyPromise: Promise<void> | null;
};

type ParserWorkerOp =
  | 'ping'
  | 'preflightCsvFile'
  | 'preflightExcelFile'
  | 'startCsvImport'
  | 'startExcelImport'
  | 'connectImport'
  | 'disposeImport';

let workerState: ParserWorkerState | undefined;

function normalizeWorkerFailure(error: unknown): Error {
  const limit = readImportLimitDetails(error);
  if (limit) return new ImportLimitError(limit);
  if (error instanceof Error) return error;
  return new DatabaseImportParserWorkerUnavailableError();
}

function resetParserWorker(state: ParserWorkerState): void {
  if (workerState === state) {
    workerState = undefined;
  }
  state.client.destroy();
  state.worker.terminate();
}

function createParserWorkerState(): ParserWorkerState {
  const worker = new Worker(getWorkerUrl('database-import-parser'));
  const client = new OpClient<DatabaseImportParserWorkerOps>(worker);
  let rejectFatal: (error: Error) => void = () => {};
  const state: ParserWorkerState = {
    worker,
    client,
    fatalPromise: new Promise<never>((_, reject) => {
      rejectFatal = reject;
    }),
    fatalError: null,
    readyPromise: null,
  };
  const handleFatalWorkerError = () => {
    const error = new DatabaseImportParserWorkerUnavailableError();
    state.fatalError = error;
    rejectFatal(error);
    resetParserWorker(state);
  };
  worker.addEventListener('error', handleFatalWorkerError);
  worker.addEventListener('messageerror', handleFatalWorkerError);
  return state;
}

function getParserWorkerState(): ParserWorkerState {
  workerState ??= createParserWorkerState();
  return workerState;
}

function createAbortError(): Error {
  const error = new Error('Import was cancelled.');
  error.name = 'AbortError';
  return error;
}

function getAbortError(signal?: AbortSignal): Error {
  return signal?.reason instanceof Error ? signal.reason : createAbortError();
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw getAbortError(signal);
  }
}

function withAbortSignal<T>(
  promise: Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  if (!signal) {
    return promise;
  }
  if (signal.aborted) {
    return Promise.reject(getAbortError(signal));
  }

  return new Promise<T>((resolve, reject) => {
    const rejectWithAbort = () => reject(getAbortError(signal));
    signal.addEventListener('abort', rejectWithAbort, { once: true });
    promise.then(resolve, reject).finally(() => {
      signal.removeEventListener('abort', rejectWithAbort);
    });
  });
}

function createProgressToken(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

function isParserProgressMessage(
  message: unknown
): message is DatabaseImportParserProgress {
  return (
    !!message &&
    typeof message === 'object' &&
    'type' in message &&
    message.type === 'database-import-parser-progress' &&
    'token' in message &&
    typeof message.token === 'string'
  );
}

function addProgressListener(
  state: ParserWorkerState,
  token: string,
  onProgress: DatabaseImportParserProgressHandler
): () => void {
  const handleProgress = (event: MessageEvent<unknown>) => {
    const message = event.data;
    if (!isParserProgressMessage(message) || message.token !== token) return;
    onProgress(message);
  };
  state.worker.addEventListener('message', handleProgress);
  return () => state.worker.removeEventListener('message', handleProgress);
}

async function waitForParserWorkerReady(
  state: ParserWorkerState
): Promise<void> {
  if (state.fatalError) {
    throw state.fatalError;
  }
  if (!state.readyPromise) {
    state.readyPromise = Promise.race([
      state.client.call('ping'),
      state.fatalPromise,
    ])
      .then(() => undefined)
      .catch(error => {
        resetParserWorker(state);
        throw normalizeWorkerFailure(error);
      });
  }

  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new DatabaseImportParserWorkerUnavailableError());
    }, WORKER_READY_TIMEOUT_MS);
    state.readyPromise
      ?.then(resolve)
      .catch(reject)
      .finally(() => {
        clearTimeout(timeout);
      });
  }).catch(error => {
    if (error instanceof DatabaseImportParserWorkerUnavailableError) {
      resetParserWorker(state);
    }
    throw normalizeWorkerFailure(error);
  });
}

function callParserWorker(
  op: 'connectImport',
  payload: { runId: string },
  signal?: AbortSignal
): Promise<MessagePort>;
function callParserWorker(
  op: 'startCsvImport',
  payload: CsvDatabaseImportWorkerStartRequest,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<DatabaseImportWorkerStartResult>;
function callParserWorker(
  op: 'preflightCsvFile',
  payload: CsvDatabaseImportWorkerPreflightRequest,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<ImportSheetPreflight>;
function callParserWorker(
  op: 'startExcelImport',
  payload: ExcelDatabaseImportWorkerStartRequest,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<DatabaseImportWorkerStartResult>;
function callParserWorker(
  op: 'preflightExcelFile',
  payload: ExcelDatabaseImportWorkerPreflightRequest,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<ImportFilePreflight>;
function callParserWorker(
  op: 'disposeImport',
  payload: { runId: string },
  signal?: AbortSignal
): Promise<void>;
async function callParserWorker(
  op: Exclude<ParserWorkerOp, 'ping'>,
  payload: unknown,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<any> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await callParserWorkerOnce(
        getParserWorkerState(),
        op,
        payload,
        signal,
        onProgress
      );
    } catch (error) {
      lastError = normalizeWorkerFailure(error);
      if (
        signal?.aborted ||
        !(lastError instanceof DatabaseImportParserWorkerUnavailableError)
      ) {
        throw lastError;
      }
    }
  }
  throw lastError ?? new DatabaseImportParserWorkerUnavailableError();
}

async function callParserWorkerOnce(
  state: ParserWorkerState,
  op: Exclude<ParserWorkerOp, 'ping'>,
  payload: unknown,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<any> {
  const progressToken = onProgress ? createProgressToken() : undefined;
  const removeProgressListener =
    progressToken && onProgress
      ? addProgressListener(state, progressToken, onProgress)
      : undefined;

  if (progressToken && onProgress) {
    onProgress({
      type: 'database-import-parser-progress',
      token: progressToken,
      phase: 'worker-starting',
    });
  }

  try {
    throwIfAborted(signal);
    await waitForParserWorkerReady(state);
    throwIfAborted(signal);
    if (progressToken && onProgress) {
      onProgress({
        type: 'database-import-parser-progress',
        token: progressToken,
        phase: 'worker-ready',
      });
    }
    if (state.fatalError) {
      throw state.fatalError;
    }
    const call = state.client.call.bind(state.client) as unknown as (
      op: Exclude<ParserWorkerOp, 'ping'>,
      payload: unknown,
      signal?: AbortSignal
    ) => Promise<any>;
    const payloadWithProgress =
      progressToken && payload && typeof payload === 'object'
        ? { ...payload, progressToken }
        : payload;

    const workerCall = signal
      ? call(op, payloadWithProgress, signal)
      : call(op, payloadWithProgress);

    return await Promise.race([
      withAbortSignal(workerCall, signal),
      state.fatalPromise,
    ]).catch(error => {
      if (error instanceof DatabaseImportParserWorkerUnavailableError) {
        resetParserWorker(state);
      }
      throw normalizeWorkerFailure(error);
    });
  } finally {
    removeProgressListener?.();
  }
}

export async function startCsvImportInWorker(
  input: CsvDatabaseImportWorkerStartRequest,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<DatabaseImportWorkerStartResult> {
  return callParserWorker('startCsvImport', input, signal, onProgress);
}

export async function preflightCsvFileInWorker(
  input: CsvDatabaseImportWorkerPreflightRequest,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<ImportSheetPreflight> {
  return callParserWorker('preflightCsvFile', input, signal, onProgress);
}

export async function startExcelImportInWorker(
  input: ExcelDatabaseImportWorkerStartRequest,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<DatabaseImportWorkerStartResult> {
  return callParserWorker('startExcelImport', input, signal, onProgress);
}

export async function preflightExcelFileInWorker(
  input: ExcelDatabaseImportWorkerPreflightRequest,
  signal?: AbortSignal,
  onProgress?: DatabaseImportParserProgressHandler
): Promise<ImportFilePreflight> {
  return callParserWorker('preflightExcelFile', input, signal, onProgress);
}

export async function disposeDatabaseImportWorkerRun(
  runId: string
): Promise<void> {
  await callParserWorker('disposeImport', { runId });
}

export function connectDatabaseImportProducer(
  runId: string,
  signal?: AbortSignal
): Promise<MessagePort> {
  return callParserWorker('connectImport', { runId }, signal);
}
